<template>
  <div class="col-md-6">
    <div class="card shadow-lg h-100">
      <div class="card-header bg-primary text-white">
        <h5 class="card-title mb-0 d-flex align-items-center">
          <i class="fas fa-file-upload me-2"></i>
          Documentos Necessários
        </h5>
      </div>
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Carregando...</span>
          </div>
          <p class="mt-2 text-muted">Carregando documentos necessários...</p>
        </div>

        <div v-else-if="requiredDocuments.length === 0" class="text-center py-4">
          <i class="fas fa-check-circle text-success fa-3x mb-3"></i>
          <p class="text-muted">Nenhum documento adicional necessário para este agendamento.</p>
        </div>

        <div v-else>
          <div class="alert alert-info d-flex align-items-center mb-3" role="alert">
            <i class="fas fa-info-circle me-2"></i>
            <small>
              Para agendar com <strong>{{ schedule.insurance.insurance_name }}</strong>, 
              por favor, envie os documentos abaixo:
            </small>
          </div>

          <div class="document-list">
            <div 
              v-for="(doc, index) in requiredDocuments" 
              :key="doc.id" 
              class="document-item mb-3"
            >
              <div class="d-flex align-items-start">
                <div class="document-icon me-3">
                  <i class="fas fa-file-pdf text-danger" v-if="doc.accepted_formats?.includes('pdf')"></i>
                  <i class="fas fa-file-image text-info" v-else></i>
                </div>
                
                <div class="flex-grow-1">
                  <h6 class="document-title mb-1">{{ doc.name }}</h6>
                  <p class="document-description text-muted small mb-2">{{ doc.description }}</p>
                  
                  <div class="document-upload-area">
                    <input 
                      type="file" 
                      :id="`file-${doc.id}`"
                      :ref="`file-${doc.id}`"
                      @change="handleFileUpload($event, doc)"
                      :accept="doc.accepted_formats"
                      class="form-control form-control-sm file-input-mobile"
                      :class="{ 'is-valid': getUploadedFile(doc.id), 'is-invalid': hasError(doc.id) }"
                    />
                    
                  </div>

                  <div v-if="getUploadedFile(doc.id)" class="uploaded-file mt-2">
                    <div class="d-flex align-items-center justify-content-between bg-light rounded p-2">
                      <div class="d-flex align-items-center flex-grow-1 min-w-0 me-2">
                        <i class="fas fa-check-circle text-success me-2 flex-shrink-0"></i>
                        <span class="small text-truncate file-name" :title="getUploadedFile(doc.id).name">
                          {{ getUploadedFile(doc.id).name }}
                        </span>
                        <span class="badge bg-success ms-2 flex-shrink-0">{{ formatFileSize(getUploadedFile(doc.id).size) }}</span>
                      </div>
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-danger flex-shrink-0"
                        @click="removeFile(doc.id)"
                        aria-label="Remover arquivo"
                      >
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                  </div>

                  <div v-if="hasError(doc.id)" class="invalid-feedback d-block">
                    {{ getError(doc.id) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-4">
            <div class="progress mb-2" style="height: 6px;">
              <div 
                class="progress-bar bg-success" 
                :style="{ width: uploadProgress + '%' }"
              ></div>
            </div>
            <small class="text-muted">
              {{ uploadedCount }} de {{ requiredDocuments.length }} documentos enviados
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";

export default {
  name: 'DocumentUpload',
  props: {
    schedule: {
      type: Object,
      required: true
    },
    specGroup: {
      default: undefined
    }
  },
  emits: ['has-required-documents'],
  data() {
    return {
      loading: false,
      requiredDocuments: [],
      uploadedFiles: {},
      errors: {}
    }
  },
  computed: {
    resolvedSpecGroup() {
      return this.specGroup !== undefined
        ? this.specGroup
        : this.$store.state.toSchedule.specGroup;
    },
    uploadedCount() {
      return Object.keys(this.uploadedFiles).length;
    },
    uploadProgress() {
      if (this.requiredDocuments.length === 0) return 100;
      return Math.round((this.uploadedCount / this.requiredDocuments.length) * 100);
    },
    hasRequiredDocuments() {
      return this.requiredDocuments.length > 0;
    }
  },
  watch: {
    hasRequiredDocuments(newValue) {
      this.$emit('has-required-documents', newValue);
    }
  },
  mounted() {
    this.loadRequiredDocuments();
  },
  methods: {
    async loadRequiredDocuments() {
      if (!this.schedule.insurance?.id_insurance || !this.schedule.professional?.id_place) {
        return;
      }

      this.loading = true;
      try {
        const response = await http.get(`/places/${this.schedule.professional.id_place}/insurances/${this.schedule.insurance.id_insurance}/documents`, {
          params: {
            id_spec_group: this.resolvedSpecGroup?.id_spec_group
          }
        });
        
        this.requiredDocuments = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar documentos:', error);
        Alert.error('Erro ao carregar lista de documentos necessários.');
      } finally {
        this.loading = false;
      }
    },

    handleFileUpload(event, doc) {
      const file = event.target.files[0];
      if (!file) return;

      // Validação de tamanho
      if (file.size > doc.max_size * 1024 * 1024) {
        this.setError(doc.id, `Arquivo muito grande. Máximo ${doc.max_size}MB.`);
        return;
      }

      // Validação de formato (apenas se accepted_formats estiver definido)
      if (doc.accepted_formats && doc.accepted_formats.trim()) {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        const acceptedFormats = doc.accepted_formats.split(',').map(f => f.trim());
        if (!acceptedFormats.some(format => file.name.toLowerCase().endsWith(format.toLowerCase()))) {
          this.setError(doc.id, `Formato não aceito. Use: ${doc.accepted_formats}`);
          return;
        }
      }

      // Limpar erro e adicionar arquivo
      this.clearError(doc.id);
      this.uploadedFiles[doc.id] = file;
      
      // Emitir evento para o componente pai
      this.$emit('documents-changed', this.getFilesForSubmission());
    },

    removeFile(docId) {
      delete this.uploadedFiles[docId];
      this.clearError(docId);
      
      // Limpar input
      const input = this.$refs[`file-${docId}`];
      if (input && input[0]) {
        input[0].value = '';
      }
      
      this.$emit('documents-changed', this.getFilesForSubmission());
    },

    getUploadedFile(docId) {
      return this.uploadedFiles[docId];
    },

    hasError(docId) {
      return !!this.errors[docId];
    },

    getError(docId) {
      return this.errors[docId];
    },

    setError(docId, message) {
      this.errors[docId] = message;
    },

    clearError(docId) {
      delete this.errors[docId];
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    getFilesForSubmission() {
      const files = [];
      for (const [docId, file] of Object.entries(this.uploadedFiles)) {
        // Encontrar o documento pelo ID para pegar o nome
        const doc = this.requiredDocuments.find(d => d.id == docId);
        files.push({
          name: doc ? doc.name : file.name,
          file: file
        });
      }
      return files;
    },

    validateAllDocuments() {
      let isValid = true;
      
      for (const doc of this.requiredDocuments) {
        if (!this.uploadedFiles[doc.id]) {
          this.setError(doc.id, 'Este documento é obrigatório.');
          isValid = false;
        }
      }
      
      return isValid;
    }
  }
}
</script>

<style scoped>
.document-item {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  background: #f8f9fa;
  transition: all 0.3s ease;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.document-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
}

.document-icon {
  font-size: 1.5rem;
  width: 2rem;
  text-align: center;
}

.document-title {
  color: #495057;
  font-weight: 600;
}

.document-description {
  font-size: 0.875rem;
  line-height: 1.4;
}

.document-upload-area {
  margin-top: 0.5rem;
  width: 100%;
  overflow: hidden;
}

.document-upload-area input[type="file"] {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.file-input-mobile {
  font-size: 0.875rem;
}


.uploaded-file {
  animation: slideIn 0.3s ease;
}

.uploaded-file .file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1 1 auto;
}

.uploaded-file .d-flex {
  gap: 0.5rem;
}

@media (max-width: 576px) {
  .document-item {
    padding: 0.75rem;
  }
  
  .document-title {
    font-size: 0.95rem;
  }
  
  .document-description {
    font-size: 0.75rem;
  }
  
  .file-input-mobile {
    font-size: 0.7rem;
    padding: 0.25rem 0.4rem;
    line-height: 1.4;
    max-width: 100%;
    overflow: hidden;
  }
  
  .file-input-mobile::-webkit-file-upload-button {
    padding: 0.25rem 0.6rem;
    font-size: 0.7rem;
    margin-right: 0.5rem;
    border-radius: 0.25rem;
  }
  
  .file-input-mobile::file-selector-button {
    padding: 0.25rem 0.6rem;
    font-size: 0.7rem;
    margin-right: 0.5rem;
    border-radius: 0.25rem;
  }
  
  .uploaded-file .file-name {
    max-width: 120px;
    font-size: 0.75rem;
  }
  
  .uploaded-file .badge {
    font-size: 0.65rem;
    padding: 0.2em 0.4em;
    white-space: nowrap;
  }
  
  .uploaded-file .btn-sm {
    padding: 0.25rem 0.4rem;
    font-size: 0.75rem;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.progress {
  border-radius: 3px;
}

.card {
  border: none;
  border-radius: 12px;
}

.card-header {
  border-radius: 12px 12px 0 0 !important;
  border: none;
}

.alert-info {
  background-color: #e3f2fd;
  border-color: #bbdefb;
  color: #1565c0;
}

.form-control.is-valid {
  border-color: #28a745;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3e%3cpath fill='%2328a745' d='m2.3 6.73.94-.94 1.78 1.78 3.5-3.5.94.94L4.02 9.67z'/%3e%3c/svg%3e");
}

.form-control.is-invalid {
  border-color: #dc3545;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath d='m5.8 4.6 2.4 2.4m0-2.4-2.4 2.4'/%3e%3c/svg%3e");
}
</style>
